import { Link } from "react-router-dom";
import { Instagram, Facebook, Linkedin, Phone, Mail, MapPin } from "lucide-react";
import { useLang } from "@/i18n/LanguageProvider";
import { buildPath } from "@/lib/routes";
import { doctor, socials, locations, LOGO } from "@/data/site";
import { TikTokIcon, OrcidIcon } from "@/components/common/BrandIcons";

const EXPLORE = ["about", "publications", "practice", "articles", "faq", "contact", "book"];
const LEGAL = ["cancellation", "payment", "privacy", "terms", "cookies"];

export function Footer() {
  const { lang, t } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-line bg-ice-soft" data-testid="site-footer">
      <div className="container-max pt-16 pb-28 md:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          {/* NAP */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3">
              <img
                src={LOGO.mark}
                alt=""
                aria-hidden="true"
                width="140"
                height="162"
                className="h-12 w-auto shrink-0"
                data-testid="footer-logo-badge"
              />
              <p className="font-serif text-2xl leading-tight text-ink">{doctor.name[lang]}</p>
            </div>
            <p className="mt-1 text-sm uppercase tracking-[0.18em] text-petrol">{doctor.role[lang]} · {doctor.credentials}</p>
            <p className="mt-4 max-w-xs text-[15px] leading-relaxed text-ink-soft">{t("footer.tagline")}</p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <a href={socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-instagram" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line text-ink-soft transition-colors hover:border-petrol hover:text-petrol">
                <Instagram className="h-[18px] w-[18px]" strokeWidth={1.5} />
              </a>
              <a href={socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="social-facebook" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line text-ink-soft transition-colors hover:border-petrol hover:text-petrol">
                <Facebook className="h-[18px] w-[18px]" strokeWidth={1.5} />
              </a>
              <a href={socials.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="social-tiktok" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line text-ink-soft transition-colors hover:border-petrol hover:text-petrol">
                <TikTokIcon className="h-[17px] w-[17px]" />
              </a>
              <a href={socials.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="social-linkedin" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line text-ink-soft transition-colors hover:border-petrol hover:text-petrol">
                <Linkedin className="h-[18px] w-[18px]" strokeWidth={1.5} />
              </a>
              <a href={socials.orcid} target="_blank" rel="noopener noreferrer" aria-label="ORCID" data-testid="social-orcid" className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-line text-ink-soft transition-colors hover:border-petrol hover:text-petrol">
                <OrcidIcon className="h-[18px] w-[18px]" />
              </a>
            </div>
          </div>

          {/* Explore */}
          <div className="lg:col-span-2">
            <h3 className="text-sm uppercase tracking-[0.18em] text-ink">{t("footer.explore")}</h3>
            <ul className="mt-4 space-y-0.5">
              {EXPLORE.map((key) => (
                <li key={key}>
                  <Link to={buildPath(key, lang)} data-testid={`footer-link-${key}`} className="inline-flex min-h-11 items-center text-[15px] text-ink-soft hover:text-petrol">{t(`nav.${key}`)}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="lg:col-span-2">
            <h3 className="text-sm uppercase tracking-[0.18em] text-ink">{t("footer.legalCol")}</h3>
            <ul className="mt-4 space-y-0.5">
              {LEGAL.map((key) => (
                <li key={key}>
                  <Link to={buildPath(key, lang)} data-testid={`footer-legal-${key}`} className="inline-flex min-h-11 items-center text-[15px] text-ink-soft hover:text-petrol">{t(`legal.${key}`)}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Offices & contact — right */}
          <div className="lg:col-span-4">
            <h3 className="text-sm uppercase tracking-[0.18em] text-ink">{t("footer.locations")}</h3>
            <ul className="mt-4 space-y-4">
              {locations.map((loc) => {
                const href = loc.mapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.mapsQuery)}`;
                return (
                  <li key={loc.id} className="text-[15px] text-ink-soft">
                    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={`footer-map-${loc.id}`} className="flex min-h-11 items-start gap-2 transition-colors hover:text-petrol">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-petrol" strokeWidth={1.5} />
                      <span>
                        <span className="block text-ink">{loc.name[lang]}</span>
                        {loc.addressLine[lang]}, {loc.floor[lang]}
                      </span>
                    </a>
                  </li>
                );
              })}
              <li>
                <a href={`tel:${doctor.phone}`} data-testid="footer-phone" className="flex min-h-11 items-center gap-2 text-[15px] text-ink-soft hover:text-petrol">
                  <Phone className="h-4 w-4 text-petrol" strokeWidth={1.5} /> {doctor.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={`mailto:${doctor.email}`} data-testid="footer-email" className="flex min-h-11 items-center gap-2 break-all text-[15px] text-ink-soft hover:text-petrol">
                  <Mail className="h-4 w-4 text-petrol" strokeWidth={1.5} /> {doctor.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-2 text-[13px] text-ink md:flex-row md:items-center md:justify-between">
          <p>© {year} {doctor.name[lang]}. {t("footer.rights")}</p>
          <p className="md:text-right" data-testid="footer-license">
            {t("footer.license")}: {doctor.license}
            {lang === "en" ? ` · ${doctor.licenseBody[lang]}` : ""}
          </p>
        </div>
        <div className="mt-5 flex flex-col gap-3 border-t border-line pt-5 text-[12px] text-ink-soft/70 md:flex-row md:items-center md:justify-between">
          <p className="max-w-2xl leading-relaxed">{t("common.disclaimer")}</p>
        </div>
      </div>
    </footer>
  );
}
